import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import PortalNav from './components/PortalNav';
import PortalFooter from './components/PortalFooter';
import DocumentsSection from './components/DocumentsSection';
import AppointmentsSection from './components/AppointmentsSection';

type PortalTab = 'overview' | 'documents' | 'appointments';

export default function ClientPortalPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<PortalTab>('overview');
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/login');
        return;
      }

      // Get user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', session.user.id)
        .single();

      if (profile?.full_name) {
        setUserName(profile.full_name);
      }

      setLoading(false);
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        navigate('/login');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00e5c7] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-[#5a6a7e]">Loading...</p>
        </div>
      </div>
    );
  }

  const tabs: { key: PortalTab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
    { key: 'documents', label: 'Documents', icon: 'ri-file-text-line' },
    { key: 'appointments', label: 'Appointments', icon: 'ri-calendar-check-line' },
  ];

  return (
    <div className="min-h-screen bg-[#f4f5f7] flex flex-col">
      <PortalNav />

      {/* Hero Banner */}
      <div className="bg-[#0a1628] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#00e5c7]/8 to-transparent rounded-full -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-[#00e5c7]/5 to-transparent rounded-full translate-y-1/2 -translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00e5c7]/30 to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-8 py-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 mb-3">
                <div className="w-8 h-px bg-[#00e5c7]"></div>
                <span className="text-[#00e5c7] text-xs uppercase tracking-[0.3em] font-semibold">
                  Client Portal
                </span>
              </div>
              <h1
                className="text-3xl md:text-4xl font-bold text-white"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Welcome Back{userName ? `, ${userName.split(' ')[0]}` : ''}
              </h1>
              <p className="text-sm text-white/50 mt-2">
                Access your documents, schedule appointments, and manage your tax services.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white/5 border border-white/10 rounded-lg px-5 py-3 text-center">
                <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>8</p>
                <p className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">Documents</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg px-5 py-3 text-center">
                <p className="text-2xl font-bold text-[#00e5c7]" style={{ fontFamily: "'Outfit', sans-serif" }}>2</p>
                <p className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">Upcoming</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex gap-0">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? 'border-[#00e5c7] text-[#0a1628]'
                    : 'border-transparent text-[#5a6a7e] hover:text-[#0a1628]'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`}></i>
                </div>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-8 py-10 w-full">
        {activeTab === 'overview' && <OverviewContent onNavigate={setActiveTab} />}
        {activeTab === 'documents' && <DocumentsSection />}
        {activeTab === 'appointments' && <AppointmentsSection />}
      </main>

      <PortalFooter />
    </div>
  );
}

function OverviewContent({ onNavigate }: { onNavigate: (tab: PortalTab) => void }) {
  const quickActions = [
    {
      icon: 'ri-file-upload-line',
      title: 'Your Documents',
      description: 'View and download your tax returns, income statements, and other important files.',
      action: 'View Documents',
      tab: 'documents' as PortalTab,
      accent: true,
    },
    {
      icon: 'ri-calendar-line',
      title: 'Schedule Appointment',
      description: 'Book a consultation with your tax advisor for strategy reviews or tax planning.',
      action: 'Book Now',
      tab: 'appointments' as PortalTab,
      accent: false,
    },
    {
      icon: 'ri-phone-line',
      title: 'Contact Your Advisor',
      description: 'Reach out directly to Alejandro Monge for urgent tax questions or concerns.',
      action: 'Call Now',
      tab: 'overview' as PortalTab,
      accent: false,
      isPhone: true,
    },
  ];

  return (
    <div>
      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6 mb-10">
        {quickActions.map((item, idx) => (
          <div
            key={idx}
            className={`rounded-lg p-7 transition-all duration-300 hover:-translate-y-1 group ${
              item.accent
                ? 'bg-[#0a1628] text-white shadow-xl shadow-[#0a1628]/15'
                : 'bg-white border border-gray-200 hover:border-[#00e5c7]/30 hover:shadow-lg'
            }`}
          >
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center mb-5 ${
                item.accent
                  ? 'bg-[#00e5c7]/15'
                  : 'bg-[#0a1628]/5 group-hover:bg-[#00e5c7]/10'
              } transition-colors`}
            >
              <i
                className={`${item.icon} text-xl ${
                  item.accent
                    ? 'text-[#00e5c7]'
                    : 'text-[#0a1628] group-hover:text-[#00e5c7]'
                } transition-colors`}
              ></i>
            </div>
            <h3
              className={`text-lg font-bold mb-2 ${
                item.accent ? 'text-white' : 'text-[#0a1628]'
              }`}
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {item.title}
            </h3>
            <p
              className={`text-sm leading-relaxed mb-5 ${
                item.accent ? 'text-white/60' : 'text-[#5a6a7e]'
              }`}
            >
              {item.description}
            </p>
            {item.isPhone ? (
              <a
                href="tel:7147669527"
                className={`inline-flex items-center text-sm font-semibold transition-colors cursor-pointer ${
                  item.accent
                    ? 'text-[#00e5c7] hover:text-[#00ccb0]'
                    : 'text-[#0a1628] group-hover:text-[#00e5c7]'
                }`}
              >
                {item.action}
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            ) : (
              <button
                onClick={() => onNavigate(item.tab)}
                className={`inline-flex items-center text-sm font-semibold transition-colors cursor-pointer ${
                  item.accent
                    ? 'text-[#00e5c7] hover:text-[#00ccb0]'
                    : 'text-[#0a1628] group-hover:text-[#00e5c7]'
                }`}
              >
                {item.action}
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Documents */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3
              className="text-base font-bold text-[#0a1628]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Recent Documents
            </h3>
            <button
              onClick={() => onNavigate('documents')}
              className="text-xs text-[#00e5c7] hover:text-[#00ccb0] font-semibold uppercase tracking-wider cursor-pointer"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {[
              { name: '2024 Federal Tax Return (1040)', date: 'Mar 15, 2025', status: 'Ready' },
              { name: '2024 State Tax Return - California', date: 'Mar 15, 2025', status: 'Ready' },
              { name: '1099-NEC Freelance Income', date: 'Jan 28, 2025', status: 'Processing' },
            ].map((doc, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-[#0a1628]/5 rounded-md flex-shrink-0">
                  <i className="ri-file-text-line text-sm text-[#0a1628]"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0a1628] truncate">
                    {doc.name}
                  </p>
                  <p className="text-xs text-[#5a6a7e]">{doc.date}</p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap ${
                    doc.status === 'Ready'
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : 'bg-amber-500/15 text-amber-400'
                  }`}
                >
                  {doc.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Appointments */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3
              className="text-base font-bold text-[#0a1628]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Upcoming Appointments
            </h3>
            <button
              onClick={() => onNavigate('appointments')}
              className="text-xs text-[#00e5c7] hover:text-[#00ccb0] font-semibold uppercase tracking-wider cursor-pointer"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {[
              { title: 'Tax Strategy Review', date: 'Jun 18', time: '10:00 AM', type: 'In-Person' },
              { title: 'Quarterly Estimated Tax Planning', date: 'Jul 2', time: '2:00 PM', type: 'Video Call' },
            ].map((apt, idx) => (
              <div
                key={idx}
                className="flex items-center gap-4 p-3 rounded-md hover:bg-gray-50 transition-colors"
              >
                <div className="w-12 h-12 flex-shrink-0 bg-[#0a1628] rounded-lg flex flex-col items-center justify-center">
                  <span
                    className="text-sm font-bold text-white leading-none"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {apt.date.split(' ')[1]}
                  </span>
                  <span className="text-[8px] uppercase tracking-wider text-[#00e5c7] font-semibold">
                    {apt.date.split(' ')[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0a1628]">
                    {apt.title}
                  </p>
                  <p className="text-xs text-[#5a6a7e]">
                    {apt.time} &middot; {apt.type}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tax Season Timeline */}
      <div className="mt-10 bg-white rounded-lg border border-gray-200 p-8">
        <h3
          className="text-base font-bold text-[#0a1628] mb-6"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          2025 Tax Season Timeline
        </h3>
        <div className="relative">
          <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200"></div>
          <div className="grid grid-cols-5 relative">
            {[
              { date: 'Jan 15', label: 'Q4 Estimated Tax', done: true },
              { date: 'Mar 15', label: 'S-Corp / Partnership', done: true },
              { date: 'Apr 15', label: 'Individual Returns', done: false, current: true },
              { date: 'Jun 15', label: 'Q2 Estimated Tax', done: false },
              { date: 'Oct 15', label: 'Extension Deadline', done: false },
            ].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center text-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                    item.done
                      ? 'bg-[#00e5c7]'
                      : item.current
                      ? 'bg-[#0a1628] ring-4 ring-[#00e5c7]/20'
                      : 'bg-gray-200'
                  }`}
                >
                  {item.done ? (
                    <i className="ri-check-line text-sm text-[#0a1628]"></i>
                  ) : (
                    <div
                      className={`w-2 h-2 rounded-full ${
                        item.current ? 'bg-[#00e5c7]' : 'bg-gray-400'
                      }`}
                    ></div>
                  )}
                </div>
                <p
                  className={`text-sm font-bold mt-3 ${
                    item.current ? 'text-[#0a1628]' : item.done ? 'text-[#00e5c7]' : 'text-[#5a6a7e]'
                  }`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {item.date}
                </p>
                <p className="text-xs text-[#5a6a7e] mt-1 max-w-[100px]">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
